import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { hasPermission } from 'utils/common';
import { Box, Typography } from '@mui/material';
import { AppConfig } from 'AppConfig';
import Carousel from 'common/bstCarousel';
import { store } from 'store/store';
import * as Actions from './actions/action';
import * as ActionFo from '../actions/actions';
import * as UserTypes from 'features/user/userTypes';
import useWindowDimensions from 'common/hooks/useWindowDimensions';
import NoAvailableReports from 'features/frontoffice/component/noAvailableReports';
import { SAVED_CHIP_CATEGORIES } from 'common/Constant';
import ReportFilters, { getAccessibleFavoritedReports, getItemCategory } from '../saved/reportFilters';
import { sortReports } from 'utils/reportSort';
import ReportSectionLoading from '../component/reportSectionLoading';
import SmartCard from 'common/bstCard/smartCard';
import SmartCardsLoading from '../component/smartCardsLoading';
import HideSmartCardDialog from 'features/landing/newHome/HideSmartCardDialog';

const getSmartCardKey = (smart, index) => smart?.id || smart?.scId || smart?.displayText || index;

const FavoriteReport = ({ selectedFilterTags: selectedFilterTagsProp = [], isTablet = false, isMobile = false, searchTerm = '', sortBy, viewMode = 'grid' }) => {
  const { width } = useWindowDimensions();
  const favorites = useSelector((state) => state.favoriteReport.favoriteReports);
  const favoritesLoading = useSelector((state) => state.favoriteReport.favoritesLoading);
  const smarts = useSelector((state) => state.fo.smarts);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const permissions = useSelector((state) => state.user.permissions);
  const showDismiss = hasPermission(AppConfig.PERMISSION.FO_SMARTCARD_DISMISS, permissions);
  const sortKey = typeof sortBy === 'string' ? sortBy : sortBy?.value;
  const [selectedFilterCategories, setSelectedFilterCategories] = useState([]);
  const [showSmartCard, setShowSmartCard] = useState(false);
  const [selectedSmartCardId, setSelectedSmartCardId] = useState({ id: null, title: '' });

  useEffect(() => {
    store.dispatch({ type: UserTypes.APP_SWITCHED, payload: AppConfig.HEADER_MENU_TYPE.FO });
    store.dispatch(Actions.getFavoriteReport());
  }, [currentTenantCd]);

  useEffect(() => {
    if (smarts === null || smarts === undefined) {
      store.dispatch(ActionFo.getFoDetail(currentTenantCd));
    }
  }, [currentTenantCd, smarts]);

  const allFavorited = useMemo(() => {
    return getAccessibleFavoritedReports(favorites, permissions);
  }, [favorites, permissions]);

  const [data, setData] = useState(() => allFavorited);

  useEffect(() => {
    setData(allFavorited);
  }, [allFavorited]);

  const searchFiltered = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    const term = (searchTerm || '').trim().toLowerCase();
    const baseList = sortKey === 'recently_saved' ? list.filter((e) => e.favorited === true) : list;
    if (!term) return baseList;
    return baseList.filter((item) => (item?.displayText || '').toLowerCase().includes(term));
  }, [data, searchTerm , sortKey]);

  const searchedSmarts = searchFiltered;
  const showNoSmartCards = !favoritesLoading && searchedSmarts?.length === 0;

    const sortedData = useMemo(
      () => sortReports(searchFiltered, sortKey), 
      [searchFiltered, sortKey]
    );

  const smartCardsGridKey = useMemo(
    () => [sortKey || 'default', ...(searchedSmarts ?? []).map((smart, index) => getSmartCardKey(smart, index))].join('|'),
    [searchedSmarts, sortKey]
  );

  const handleHideSmartCard = () => {
      if (!selectedSmartCardId?.id) return;
      store.dispatch(ActionFo.dismissReport(selectedSmartCardId?.id));
      setShowSmartCard(false);
      setSelectedSmartCardId({ id: null, title: '' });
  };
  
  const itemsForCategory = (category) =>
    Array.isArray(sortedData)
      ? sortedData.filter((item) => category.codes.includes(getItemCategory(item)))
      : [];

  const renderCategorySections = (categoriesSelected) => {
    const isAll = !Array.isArray(categoriesSelected) || categoriesSelected.length === 0;

    if (isAll) {
      return SAVED_CHIP_CATEGORIES.map((category) => {
        const items = itemsForCategory(category);
        const itemsForDismissed = items.filter((item) => !item?.dismissed);
        if (!items.length) return null;
        return (
          <Box key={category.key} sx={{ mb: 4 }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Rubik',
                fontSize: '18px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '27px',
                color: '#000000',
                mt: '40px',
                mb: '24px'
              }}
            >
              {category.label} ({category.key === 'smart' ? itemsForDismissed.length : items.length})
            </Typography>
            {!items?.length ? (
            <NoAvailableReports />
             ) : category.key === 'smart' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '24px',
                width: '100%'
              }}>
              <Box sx={{ width: '100%' }}>
                {favoritesLoading || smarts === null || smarts === undefined ? (
                  <SmartCardsLoading count={6} variant="grid" />
                ) : showNoSmartCards ? (
                  <NoAvailableReports isSmartCards />
                ) : (
                  <Box
                    key={smartCardsGridKey}
                    sx={{
                      display: viewMode === 'list' ? 'flex' : 'inline-grid',
                      flexDirection: viewMode === 'list' ? 'column' : undefined,
                      width: '100%',
                      rowGap: viewMode === 'list' ? '16px' : '24px',
                      columnGap: '24px',
                      alignSelf: 'stretch',
                      gridTemplateRows: viewMode === 'list' ? undefined : 'repeat(4, fit-content(100%))',
                      gridTemplateColumns: viewMode === 'list' ? undefined : { md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
                    }}>
                    {itemsForDismissed?.map((smart, idx) => (
                      <Box
                        key={smart.id || idx}
                        sx={{
                          boxSizing: 'border-box',
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%'
                        }}>
                        <SmartCard
                          item={smarts.find((item) => String(getSmartCardKey(item)) === String(getSmartCardKey(smart))) || smart}
                          reportType={AppConfig.SMART_CAROUSEL}
                          showDismiss={showDismiss}
                          permissions={permissions}
                          setShowSmartCard={setShowSmartCard}
                          setSelectedSmartCardId={setSelectedSmartCardId}
                          viewMode={viewMode}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
              ) : (
                <Carousel
                  items={items}
                  type={AppConfig.FAVORITE_CAROUSEL}
                  viewWidth={width}
                  cardWidth={AppConfig.HOME_RECOMMEND_CARD_WIDTH}
                  cardHeight={AppConfig.HOME_RECOMMEND_CARD_HEIGHT}
                  isTablet={isTablet}
                  viewMode={viewMode}
                />
              )
            }
          </Box>
        );
      });
    }

    return categoriesSelected.map((category) => {

      const items = itemsForCategory(category);
      const itemsForDismissed = items.filter((item) => !item?.dismissed);
      return (
        <Box key={category.key} sx={{ mb: 4 }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Rubik',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '27px',
              color: '#000000',
              mt: '40px',
              mb: '24px'
            }}
          >
            {category.label} ({category.key === 'smart' ? itemsForDismissed.length : items.length})
          </Typography>
          {!items?.length ? (
            <NoAvailableReports />
          ) : category.key === 'smart' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '24px',
                width: '100%'
              }}>
              <Box sx={{ width: '100%' }}>
                {favoritesLoading || smarts === null || smarts === undefined ? (
                  <SmartCardsLoading count={6} variant="grid" />
                ) : showNoSmartCards ? (
                  <NoAvailableReports isSmartCards />
                ) : (
                  <Box
                    key={smartCardsGridKey}
                    sx={{
                      display: viewMode === 'list' ? 'flex' : 'inline-grid',
                      flexDirection: viewMode === 'list' ? 'column' : undefined,
                      width: '100%',
                      rowGap: viewMode === 'list' ? '16px' : '24px',
                      columnGap: '24px',
                      alignSelf: 'stretch',
                      gridTemplateRows: viewMode === 'list' ? undefined : 'repeat(4, fit-content(100%))',
                      gridTemplateColumns: viewMode === 'list' ? undefined : { md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
                    }}>
                    {itemsForDismissed.map((smart, idx) => (
                      <Box
                        key={getSmartCardKey(smart, idx)}
                        sx={{
                          boxSizing: 'border-box',
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%'
                        }}>
                        <SmartCard
                          item={smarts.find((item) => String(getSmartCardKey(item)) === String(getSmartCardKey(smart))) || smart}
                          reportType={AppConfig.SMART_CAROUSEL}
                          showDismiss={showDismiss}
                          permissions={permissions}
                          setShowSmartCard={setShowSmartCard}
                          setSelectedSmartCardId={setSelectedSmartCardId}
                          viewMode={viewMode}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
            ) : (
              <Carousel
                items={items}
                type={AppConfig.FAVORITE_CAROUSEL}
                viewWidth={width}
                cardWidth={AppConfig.HOME_RECOMMEND_CARD_WIDTH}
                cardHeight={AppConfig.HOME_RECOMMEND_CARD_HEIGHT}
                isTablet={isTablet}
                viewMode={viewMode}
              />
            )
          }
        </Box>
      );
    });
  };

  const selectedCategories = isMobile ? selectedFilterTagsProp : selectedFilterCategories;

  return (
    <>
    {favoritesLoading ? (
      <ReportSectionLoading count={10} variant="grid" />
    ) :
      !allFavorited.length ? (
        <NoAvailableReports />
      ) : (
        <>
          {!isMobile && (
            <Box sx={{ mb: 3 }}>
              <ReportFilters
                setData={setData}
                onSelectionChange={setSelectedFilterCategories}
                isMobile={isMobile}
              />
            </Box>
          )}
          {sortedData.length ? (
            renderCategorySections(selectedCategories)
          ) : (
            <NoAvailableReports />
          )}
        </>
      )}
       {showSmartCard && (
        <HideSmartCardDialog
          open={showSmartCard}
          onClose={() => setShowSmartCard(false)}
          handleHideSmartCard={handleHideSmartCard}
          selectedSmartCardId={selectedSmartCardId}
        />
      )}
    </>
    
  );
};

export default FavoriteReport;
